import React, { useEffect, useState } from "react";
import { 
    Box, Grid, Typography, Switch, TextField, Paper, Divider, 
    IconButton, Tooltip, FormControlLabel, Stack 
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

import { useTranslation } from "react-i18next";

// IMPORTAMOS EL CONTROLADOR
import { GetSchedules, SaveSchedule } from '../../controllers/company/SchedulesController';

import QUDProgress from "../progress";
import ResponseComponent from "../responseComponent"; // Ajusta ruta


const defaultShift = () => ({ start_time: '09:00', end_time: '17:00' });

// PROPS:
// resourceType: 'merchant', 'asset', 'employee'
// resourceId: El ID numérico (ej: 1, 50, 8)
export default function WeeklyScheduleEditor({ resourceType, resourceId }) {
    const { t } = useTranslation();
    const [schedule, setSchedule] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [response, setResponse] = useState({ show: false, success: false, title: '', message: '' });

    const daysList = [
        { id: 1, label: t('days.1') }, // Lunes
        { id: 2, label: t('days.2') },
        { id: 3, label: t('days.3') },
        { id: 4, label: t('days.4') },
        { id: 5, label: t('days.5') },
        { id: 6, label: t('days.6') },
        { id: 0, label: t('days.0') }, // Domingo
    ];

    useEffect(() => {
        if (resourceId && resourceType) {
            handleGetSchedules();
        }
    }, [resourceId, resourceType]);

    // --- CARGA DE DATOS ---
    const handleGetSchedules = async () => {
        setIsLoading(true);
        const res = await GetSchedules(resourceType, resourceId);
        if (res.success) {
            mergeScheduleData(res.data);
        } else {
            setSchedule(generateEmptyWeek());
        }
        setIsLoading(false);
    };

    const generateEmptyWeek = () => {
        return daysList.map(d => ({
            day_of_week: d.id,
            label: d.label,
            is_day_off: true,
            shifts: [defaultShift()]
        }));
    };

    const mergeScheduleData = (dbData) => {
        const weekStructure = generateEmptyWeek();

        dbData.forEach(row => {
            // Usamos == para comparar string "1" con number 1 por seguridad
            const dayIndex = weekStructure.findIndex(d => d.day_of_week == row.day_of_week);

            if (dayIndex !== -1) {
                const day = weekStructure[dayIndex];

                // Si la fila que viene de BD dice que NO es día libre (está abierto)
                if (!row.is_day_off) {
                    
                    // --- CORRECCIÓN AQUÍ ---
                    // Si el día en nuestra estructura local TODAVÍA figura como "Cerrado" (is_day_off: true),
                    // significa que es el primer turno que encontramos para este día en el bucle.
                    // Entonces: Lo abrimos y BORRAMOS el turno dummy por defecto.
                    if (day.is_day_off) {
                        day.is_day_off = false; 
                        day.shifts = []; // Limpiamos el array para empezar a meter los reales
                    }

                    const newShift = {
                        start_time: row.start_time ? row.start_time.substring(0,5) : '',
                        end_time: row.end_time ? row.end_time.substring(0,5) : ''
                    };

                    // Evitamos duplicados exactos (por si la BD trajera basura)
                    const exists = day.shifts.find(s => s.start_time === newShift.start_time && s.end_time === newShift.end_time);
                    
                    if(!exists) {
                        day.shifts.push(newShift);
                    }
                }
            }
        });
        
        setSchedule(weekStructure);
    };

    // --- MANEJADORES UI ---
    const toggleDayOff = (dayIndex) => {
        const newSchedule = [...schedule];
        newSchedule[dayIndex].is_day_off = !newSchedule[dayIndex].is_day_off;
        setSchedule(newSchedule);
    };

    const updateShift = (dayIndex, shiftIndex, field, value) => {
        const newSchedule = [...schedule];
        newSchedule[dayIndex].shifts[shiftIndex][field] = value;
        setSchedule(newSchedule);
    };

    const addShift = (dayIndex) => {
        const newSchedule = [...schedule];
        newSchedule[dayIndex].shifts.push(defaultShift());
        setSchedule(newSchedule);
    };

    const removeShift = (dayIndex, shiftIndex) => {
        const newSchedule = [...schedule];
        if (newSchedule[dayIndex].shifts.length > 1) {
            newSchedule[dayIndex].shifts.splice(shiftIndex, 1);
        } else {
             newSchedule[dayIndex].shifts[0] = { start_time: '', end_time: '' };
        }
        setSchedule(newSchedule);
    };

    // --- GUARDADO ---
    const handleSave = async () => {
        setIsSaving(true);
        const payload = [];
        schedule.forEach(day => {
            if (day.is_day_off) {
                payload.push({ day_of_week: day.day_of_week, is_day_off: true, start_time: null, end_time: null });
            } else {
                day.shifts.forEach(shift => {
                    if (shift.start_time && shift.end_time) {
                        payload.push({ day_of_week: day.day_of_week, is_day_off: false, start_time: shift.start_time, end_time: shift.end_time });
                    }
                });
            }
        });

        const res = await SaveSchedule(resourceType, resourceId, payload);

        setResponse({
            show: true,
            success: res.success,
            title: res.success ? 'Guardado' : 'Error',
            message: res.message
        });
        setIsSaving(false);
    };

    if (isLoading) return <QUDProgress open={true} />;

    return (
        <Box sx={{ width: '100%' }}>
            <ResponseComponent 
                show={response.show} success={response.success} title={response.title} message={response.message} 
                actionText="Ok" onClick={() => setResponse({...response, show: false})} 
            />

            <Box display="flex" justifyContent="flex-end" mb={2}>
                <LoadingButton variant="contained" startIcon={<SaveIcon />} loading={isSaving} onClick={handleSave}>
                    Guardar Horario
                </LoadingButton>
            </Box>

            <Paper variant="outlined">
                {schedule.map((day, dayIndex) => (
                    <Box key={day.day_of_week}>
                        <Grid container sx={{ p: 2, bgcolor: day.is_day_off ? '#fafafa' : 'white' }} alignItems="flex-start">
                            <Grid item xs={12} sm={3} md={2} sx={{ pt: 1 }}>
                                <FormControlLabel
                                    control={<Switch checked={!day.is_day_off} onChange={() => toggleDayOff(dayIndex)} color="success" />}
                                    label={<Typography fontWeight={!day.is_day_off ? 'bold' : 'normal'}>{day.label}</Typography>}
                                />
                                {day.is_day_off && <Typography variant="caption" display="block" color="text.disabled" sx={{ ml: 4 }}>{t('helpers.closed')}</Typography>}
                            </Grid>

                            <Grid item xs={12} sm={9} md={10}>
                                {!day.is_day_off ? (
                                    <Stack spacing={2}>
                                        {day.shifts.map((shift, shiftIndex) => (
                                            <Box key={shiftIndex} display="flex" alignItems="center" gap={2}>
                                                <TextField
                                                    type="time" value={shift.start_time}
                                                    onChange={(e) => updateShift(dayIndex, shiftIndex, 'start_time', e.target.value)}
                                                    size="small" sx={{ width: 140 }}
                                                />
                                                <Typography>-</Typography>
                                                <TextField
                                                    type="time" value={shift.end_time}
                                                    onChange={(e) => updateShift(dayIndex, shiftIndex, 'end_time', e.target.value)}
                                                    size="small" sx={{ width: 140 }}
                                                />
                                                <Box display="flex">
                                                    {day.shifts.length > 1 && (
                                                        <Tooltip title="Eliminar turno"><IconButton size="small" color="error" onClick={() => removeShift(dayIndex, shiftIndex)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                                    )}
                                                    {shiftIndex === day.shifts.length - 1 && (
                                                        <Tooltip title="Agregar turno"><IconButton size="small" color="primary" onClick={() => addShift(dayIndex)}><AddIcon fontSize="small" /></IconButton></Tooltip>
                                                    )}
                                                </Box>
                                            </Box>
                                        ))}
                                    </Stack>
                                ) : <Box sx={{ height: 40 }} />}
                            </Grid>
                        </Grid>
                        {dayIndex < 6 && <Divider />}
                    </Box>
                ))}
            </Paper>
        </Box>
    );
}